import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Chip,
  MenuItem,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Upload as UploadIcon,
  Code as CodeIcon,
  Edit as EditIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const DNALayerManager = ({
  brandId,
  brand,
  userId,
  segmentDNA,
  brandDNA,
  collectionDNA,
  pieceDNA,
  onUpdateSegmentDNA,
  onUpdateBrandDNA,
  onUpdateCollectionDNA,
  onUpdatePieceDNA,
}) => {
  const [availableSegments, setAvailableSegments] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [segmentSubmitting, setSegmentSubmitting] = useState(false);
  const [segmentForm, setSegmentForm] = useState({
    name: '',
    targetBodyType: 'unisex_adult',
    chestEase: '4',
    waistEase: '3',
    hipEase: '4',
  });

  useEffect(() => {
    // Load available DNA files from the backend
    loadAvailableDNA();
  }, [brandId]);

  const loadAvailableDNA = async () => {
    try {
      if (!brandId) {
        setAvailableSegments([]);
        setAvailableBrands([]);
        setAvailableCollections([]);
        return;
      }

      const params = new URLSearchParams({
        brandId,
        status: 'active',
      });

      const response = await fetch(`/api/dna-layers?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to load DNA layers (${response.status})`);
      }

      const data = await response.json();
      const layers = Array.isArray(data.layers) ? data.layers : [];

      const segments = [];
      const brands = [];
      const collections = [];

      layers.forEach((layer) => {
        const entry = {
          id: layer._id,
          name: layer.name,
          path: layer.collectionId || layer.description || layer._id,
          yaml: layer.yamlContent,
        };

        if (layer.type === 'segment') {
          segments.push(entry);
        } else if (layer.type === 'brand') {
          brands.push(entry);
        } else if (layer.type === 'collection') {
          collections.push(entry);
        }
      });

      setAvailableSegments(segments);
      setAvailableBrands(brands);
      setAvailableCollections(collections);
    } catch (error) {
      console.error('Error loading DNA files:', error);
      toast.error('Failed to load DNA libraries');
    }
  };

  const handleSelectSegment = async (segmentId) => {
    try {
      const segment = availableSegments.find(s => s.id === segmentId);
      onUpdateSegmentDNA({ id: segmentId, name: segment.name, path: segment.path, yaml: segment.yaml });
      toast.success(`Loaded ${segment.name}`);
    } catch (error) {
      console.error('Error loading segment DNA:', error);
      toast.error('Failed to load segment DNA');
    }
  };

  const handleSelectBrand = async (brandDnaId) => {
    try {
      const brandDna = availableBrands.find(b => b.id === brandDnaId);
      onUpdateBrandDNA({ id: brandDnaId, name: brandDna.name, path: brandDna.path, yaml: brandDna.yaml });
      toast.success(`Loaded ${brandDna.name}`);
    } catch (error) {
      console.error('Error loading brand DNA:', error);
      toast.error('Failed to load brand DNA');
    }
  };

  const handleSelectCollection = async (collectionId) => {
    try {
      const collection = availableCollections.find(c => c.id === collectionId);
      onUpdateCollectionDNA({ id: collectionId, name: collection.name, path: collection.path, yaml: collection.yaml });
      toast.success(`Loaded ${collection.name}`);
    } catch (error) {
      console.error('Error loading collection DNA:', error);
      toast.error('Failed to load collection DNA');
    }
  };

  const handleOpenSegmentDialog = () => {
    if (!brandId || !userId) {
      toast.error('Missing brand or user information for creating Segment DNA');
      return;
    }

    setSegmentForm({
      name: '',
      targetBodyType: 'unisex_adult',
      chestEase: '4',
      waistEase: '3',
      hipEase: '4',
    });
    setSegmentDialogOpen(true);
  };

  const handleSegmentFieldChange = (field) => (event) => {
    setSegmentForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCreateSegmentDNA = async () => {
    if (!segmentForm.name.trim()) {
      toast.error('Segment name is required');
      return;
    }

    if (!brandId || !userId) {
      toast.error('Missing brand or user information for creating Segment DNA');
      return;
    }

    const segmentName = segmentForm.name.trim();
    const segmentId = segmentName.toLowerCase().replace(/\s+/g, '_');

    const yamlContent =
      `# Segment DNA: ${segmentName}\n` +
      `segment_identity:\n` +
      `  id: ${segmentId}\n` +
      `  name: "${segmentName}"\n\n` +
      `fit_profile:\n` +
      `  target_body_type: "${segmentForm.targetBodyType}"\n` +
      `  ease_table_cm:\n` +
      `    chest: ${segmentForm.chestEase || 0}\n` +
      `    waist: ${segmentForm.waistEase || 0}\n` +
      `    hip: ${segmentForm.hipEase || 0}`;

    try {
      setSegmentSubmitting(true);

      const response = await fetch('/api/dna-layers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: segmentName,
          type: 'segment',
          brandId,
          createdBy: userId,
          yamlContent,
          description: `Segment DNA for ${segmentName}`,
          status: 'active',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create Segment DNA');
      }

      const result = await response.json();
      const layer = result.layer;

      const newEntry = {
        id: layer?._id,
        name: layer?.name || segmentName,
        path: layer?.collectionId || layer?._id || 'custom',
        yaml: layer?.yamlContent || yamlContent,
      };

      setAvailableSegments((prev) => [...prev, newEntry]);

      onUpdateSegmentDNA({
        id: newEntry.id,
        name: newEntry.name,
        path: newEntry.path,
        yaml: newEntry.yaml,
      });

      setSegmentDialogOpen(false);
      toast.success(`Created Segment DNA for ${segmentName}`);
    } catch (error) {
      console.error('Error creating segment DNA:', error);
      toast.error(error.message || 'Failed to create Segment DNA');
    } finally {
      setSegmentSubmitting(false);
    }
  };

  const handleCreatePieceDNA = async () => {
    const pieceName = prompt('Enter piece name (e.g., "Classic Crew Neck Tee"):');
    if (!pieceName) return;

    if (!brandId || !userId) {
      toast.error('Missing brand or user information for creating Piece DNA');
      return;
    }

    const yamlContent = `# Piece DNA: ${pieceName}\npiece_identity:\n  name: "${pieceName}"\n  style_number: "STYLE-${Date.now()}"\n\nconstruction_overrides:\n  # Add specific construction details here\n  ease_chest: 5\n  ease_waist: 4`;

    try {
      const response = await fetch('/api/dna-layers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: pieceName,
          type: 'piece',
          brandId,
          createdBy: userId,
          yamlContent,
          description: `Piece DNA for ${pieceName}`,
          status: 'active',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create Piece DNA');
      }

      const result = await response.json();
      const layer = result.layer;

      onUpdatePieceDNA({
        id: layer?._id,
        name: layer?.name || pieceName,
        path: layer?.collectionId || layer?._id || 'custom',
        yaml: layer?.yamlContent || yamlContent,
      });

      toast.success(`Created Piece DNA for ${pieceName}`);
    } catch (error) {
      console.error('Error creating piece DNA:', error);
      toast.error(error.message || 'Failed to create Piece DNA');
    }
  };

  const renderYamlPreview = (title, dna, actions = null) => {
    if (!dna?.yaml) return null;

    return (
      <Paper
        variant="outlined"
        sx={(theme) => ({
          mt: 2,
          px: 2,
          py: 2.5,
          borderRadius: 2,
          borderColor: 'divider',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
        })}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dna.name || 'Loaded DNA layer'}
              </Typography>
              {dna.path && (
                <Chip
                  label={dna.path}
                  size="small"
                  variant="outlined"
                  sx={{ maxWidth: 220 }}
                />
              )}
            </Stack>
            {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
          </Stack>
          <Box
            component="pre"
            sx={(theme) => ({
              fontSize: '0.8rem',
              fontFamily: theme.typography.fontFamilyMonospace || 'Menlo, Consolas, "Courier New", monospace',
              margin: 0,
              padding: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
              backgroundColor: '#0b1120',
              color: '#f1f5f9',
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#1e293b'}`,
              boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.25)',
              overflow: 'auto',
              maxHeight: 200,
              lineHeight: 1.5,
            })}
          >
            {dna.yaml}
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Configure DNA Layers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        DNA layers control how the pattern is manipulated. Each layer adds or overrides specifications from the previous layer.
        The merge order is: Pattern Block → Segment → Brand → Collection → Piece → Model Sizing.
      </Typography>

      <Alert severity="info" variant="outlined" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How DNA Layers Work:</strong> Start with Segment DNA (fit profile for category like uniforms/streetwear),
          then add Brand DNA (your brand's fit philosophy), optionally add Collection DNA (seasonal tweaks),
          and finally Piece DNA (this specific garment's details).
        </Typography>
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {/* Segment DNA */}
        <Accordion
          defaultExpanded
          square={false}
          disableGutters
          sx={{
            borderRadius: 2,
            boxShadow: '0 12px 24px -18px rgba(15,23,42,0.4), 0 0 1px rgba(15,23,42,0.12)',
            '&:before': { display: 'none' },
          }}
        >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              1. Segment DNA
            </Typography>
            {segmentDNA && <Chip label="Configured" size="small" color="success" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Segment DNA defines fit profiles for garment categories (e.g., uniforms, streetwear, activewear)
            </Typography>

            <TextField
              select
              fullWidth
              label="Select Segment"
              value={segmentDNA?.id || ''}
              onChange={(e) => handleSelectSegment(e.target.value)}
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableSegments.map((segment) => (
                <MenuItem key={segment.id} value={segment.id}>
                  {segment.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              onClick={handleOpenSegmentDialog}
              startIcon={<EditIcon />}
              size="small"
            >
              Create Segment DNA
            </Button>

            {segmentDNA && renderYamlPreview('Segment DNA preview', segmentDNA)}
          </Stack>
        </AccordionDetails>
      </Accordion>

        {/* Brand DNA */}
        <Accordion
          square={false}
          disableGutters
          sx={{
            borderRadius: 2,
            boxShadow: '0 12px 24px -18px rgba(15,23,42,0.4), 0 0 1px rgba(15,23,42,0.12)',
            '&:before': { display: 'none' },
          }}
        >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              2. Brand DNA
            </Typography>
            {brandDNA && <Chip label="Configured" size="small" color="success" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Brand DNA defines your brand's unique fit philosophy and overrides segment defaults
            </Typography>

            <TextField
              select
              fullWidth
              label="Select Brand DNA"
              value={brandDNA?.id || ''}
              onChange={(e) => handleSelectBrand(e.target.value)}
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableBrands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>

            {brandDNA && renderYamlPreview('Brand DNA preview', brandDNA)}
          </Stack>
        </AccordionDetails>
      </Accordion>

        {/* Collection DNA */}
        <Accordion
          square={false}
          disableGutters
          sx={{
            borderRadius: 2,
            boxShadow: '0 12px 24px -18px rgba(15,23,42,0.4), 0 0 1px rgba(15,23,42,0.12)',
            '&:before': { display: 'none' },
          }}
        >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                3. Collection DNA
              </Typography>
              <Chip label="Optional" size="small" />
            </Stack>
            {collectionDNA && <Chip label="Configured" size="small" color="success" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Collection DNA adds seasonal or collection-specific fit adjustments (e.g., SS25 relaxed fit)
            </Typography>

            <TextField
              select
              fullWidth
              label="Select Collection"
              value={collectionDNA?.id || ''}
              onChange={(e) => handleSelectCollection(e.target.value)}
              size="small"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableCollections.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            {collectionDNA && renderYamlPreview('Collection DNA preview', collectionDNA)}
          </Stack>
        </AccordionDetails>
      </Accordion>

        {/* Piece DNA */}
        <Accordion
          square={false}
          disableGutters
          sx={{
            borderRadius: 2,
            boxShadow: '0 12px 24px -18px rgba(15,23,42,0.4), 0 0 1px rgba(15,23,42,0.12)',
            '&:before': { display: 'none' },
          }}
        >
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              4. Piece DNA
            </Typography>
            {pieceDNA && <Chip label="Configured" size="small" color="success" />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Piece DNA contains specifications unique to this specific garment design
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="contained"
                onClick={handleCreatePieceDNA}
                startIcon={<EditIcon />}
                size="small"
              >
                Create Piece DNA
              </Button>
              <Tooltip title="Upload a YAML file with piece-specific DNA overrides">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    size="small"
                    disabled
                  >
                    Upload YAML
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {pieceDNA && renderYamlPreview(
              'Piece DNA preview',
              pieceDNA,
              <Tooltip title="Edit YAML">
                <span>
                  <IconButton size="small">
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
      </Box>

      {/* Create Segment DNA Dialog */}
      <Dialog
        open={segmentDialogOpen}
        onClose={() => !segmentSubmitting && setSegmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Segment DNA</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Segment name"
              fullWidth
              value={segmentForm.name}
              onChange={handleSegmentFieldChange('name')}
              placeholder='e.g., "Uniforms Segment"'
              autoFocus
            />
            <TextField
              select
              label="Target body type"
              fullWidth
              value={segmentForm.targetBodyType}
              onChange={handleSegmentFieldChange('targetBodyType')}
            >
              <MenuItem value="unisex_adult">Unisex Adult</MenuItem>
              <MenuItem value="womens">Womens</MenuItem>
              <MenuItem value="mens">Mens</MenuItem>
              <MenuItem value="unisex_youth">Unisex Youth</MenuItem>
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Chest ease (cm)"
                type="number"
                fullWidth
                value={segmentForm.chestEase}
                onChange={handleSegmentFieldChange('chestEase')}
              />
              <TextField
                label="Waist ease (cm)"
                type="number"
                fullWidth
                value={segmentForm.waistEase}
                onChange={handleSegmentFieldChange('waistEase')}
              />
              <TextField
                label="Hip ease (cm)"
                type="number"
                fullWidth
                value={segmentForm.hipEase}
                onChange={handleSegmentFieldChange('hipEase')}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSegmentDialogOpen(false)}
            disabled={segmentSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSegmentDNA}
            disabled={segmentSubmitting}
          >
            {segmentSubmitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary */}
      <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Layer Summary
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Segment DNA:</Typography>
            <Typography variant="body2" color={segmentDNA ? 'success.main' : 'text.secondary'}>
              {segmentDNA ? segmentDNA.name : 'Not configured'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Brand DNA:</Typography>
            <Typography variant="body2" color={brandDNA ? 'success.main' : 'text.secondary'}>
              {brandDNA ? brandDNA.name : 'Not configured'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Collection DNA:</Typography>
            <Typography variant="body2" color={collectionDNA ? 'success.main' : 'text.secondary'}>
              {collectionDNA ? collectionDNA.name : 'Optional - not set'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Piece DNA:</Typography>
            <Typography variant="body2" color={pieceDNA ? 'success.main' : 'text.secondary'}>
              {pieceDNA ? pieceDNA.name : 'Not configured'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DNALayerManager;
